import { useState } from 'react';
import { 
  Brain, 
  AlertTriangle,
  CheckCircle,
  Headphones,
  TrendingUp,
  Filter,
  Download,
  Eye,
  Shield
} from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

interface ThemisDetection {
  id: string;
  title: string;
  description: string;
  source: {
    platform: 'slack' | 'teams' | 'salesforce' | 'zendesk';
    channel: string;
    icon: any;
  };
  severity: 'critical' | 'high' | 'medium' | 'low';
  riskType: string;
  detectedAt: Date;
  keywords: string[];
  confidence: number;
  status: 'new' | 'reviewed' | 'escalated' | 'dismissed';
  assignedTo?: string;
}

const mockDetections: ThemisDetection[] = [
  {
    id: '1',
    title: 'Customer complaint about misleading product information',
    description: 'Customer states they were told the product had features that are not available. Potential consumer duty breach regarding clear communication.',
    source: {
      platform: 'zendesk',
      channel: 'Customer Complaints #4521',
      icon: Headphones,
    },
    severity: 'high',
    riskType: 'Consumer Duty',
    detectedAt: new Date('2025-11-10T14:30:00Z'),
    keywords: ['misleading', 'consumer duty', 'complaint', 'unfair'],
    confidence: 92,
    status: 'new',
  },
];

export function IntegrationsPage() {
  const { sectionColor } = useNavigation();
  const [detections] = useState<ThemisDetection[]>(mockDetections);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeverity] = useState<string>('all');

  const filteredDetections = detections.filter(d => {
    if (filterStatus !== 'all' && d.status !== filterStatus) return false;
    if (filterSeverity !== 'all' && d.severity !== filterSeverity) return false;
    return true;
  });

  const criticalCount = detections.filter(d => d.severity === 'critical').length;
  const highCount = detections.filter(d => d.severity === 'high').length;
  const newCount = detections.filter(d => d.status === 'new').length;
  const avgConfidence = Math.round(detections.reduce((sum, d) => sum + d.confidence, 0) / detections.length);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'badge-primary';
      case 'reviewed': return 'badge-info';
      case 'escalated': return 'badge-danger';
      case 'dismissed': return 'badge bg-neutral-800 text-neutral-400 border-neutral-700';
      default: return 'badge';
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'slack': return 'text-purple-400';
      case 'teams': return 'text-blue-400';
      case 'salesforce': return 'text-cyan-400';
      case 'zendesk': return 'text-green-400';
      default: return 'text-neutral-400';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
            <Brain className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className={cn("text-3xl font-bold", sectionColor)}>
              Themis Assessments
            </h1>
            <p className="text-neutral-400 mt-1">
              AI-detected risks from across your communication channels
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-outline flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button className="btn-primary flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Configure
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card border border-red-500/30 bg-red-500/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Critical Risks</p>
              <p className="text-3xl font-bold text-red-400 mt-1">{criticalCount}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="card border border-orange-500/30 bg-orange-500/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">High Priority</p>
              <p className="text-3xl font-bold text-orange-400 mt-1">{highCount}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-400" />
          </div>
        </div>

        <div className="card border border-primary-500/30 bg-primary-500/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Needs Review</p>
              <p className="text-3xl font-bold text-primary-400 mt-1">{newCount}</p>
            </div>
            <Eye className="w-8 h-8 text-primary-400" />
          </div>
        </div>

        <div className="card border border-cyan-500/30 bg-cyan-500/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Avg Confidence</p>
              <p className="text-3xl font-bold text-cyan-400 mt-1">{avgConfidence}%</p>
            </div>
            <CheckCircle className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
      </div>

      <div className="card border-2 border-primary-500/30 bg-gradient-to-br from-primary-500/5 to-cyan-500/5">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
            <Brain className="w-6 h-6 text-black" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-white">Themis Intelligence Layer</h3>
              <span className="badge badge-primary text-xs">AI-Powered</span>
            </div>
            <p className="text-sm text-neutral-300">
              Themis continuously monitors your integrated platforms for emerging risks, analyzing conversations and interactions in real-time.
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-400" />
            <span className="text-sm font-medium text-neutral-300">Filters:</span>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={cn(
                "px-3 py-1 rounded-lg text-sm transition-colors",
                filterStatus === 'all' 
                  ? 'bg-primary-500 text-black font-medium' 
                  : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
              )}
            >
              All Status
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-white">
          Risk Detections ({filteredDetections.length})
        </h2>

        {filteredDetections.map((detection) => {
          const Icon = detection.source.icon;
          return (
            <div key={detection.id} className="card hover:border-primary-500/30 transition-all">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-neutral-800 rounded-lg">
                  <Icon className={cn("w-5 h-5", getPlatformColor(detection.source.platform))} />
                </div>

                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">{detection.title}</h3>
                  <p className="text-sm text-neutral-400">{detection.description}</p>
                  
                  <div className="flex items-center gap-4 mt-2 text-sm text-neutral-500">
                    <span>{detection.source.channel}</span>
                    <span className={cn("badge text-xs", getStatusColor(detection.status))}>
                      {detection.status}
                    </span>
                  </div>
                </div>

                <button className="btn-primary text-xs px-3 py-1">
                  Create Risk
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
